import moment from 'moment';

import {
  type AtlasGqlDamageSchema,
  type AtlasGqlDamageSchemaAttributeDefinition,
  type AtlasGqlDamageSchemaAttributeDefinitionDate,
  type AtlasGqlDamageSchemaAttributeDefinitionNumber,
  type AtlasGqlDamageSchemaAttributeDefinitionString,
  AtlasGqlDamageSchemaAttributeState,
  AtlasGqlDamageSchemaAttributeType,
} from 'types/atlas-graphql';

import {
  type CsvDamageRowValidation,
  type DataField,
  type EditableCsvDamageRow,
  type HeaderMappingEntry,
  type PreviewDamage,
} from '../types';

import { isValidCoordinates, isValidDateFormat } from './Confirmation/utils';

type DamageSchemaDefinition =
  | AtlasGqlDamageSchemaAttributeDefinition
  | AtlasGqlDamageSchemaAttributeDefinitionDate
  | AtlasGqlDamageSchemaAttributeDefinitionNumber
  | AtlasGqlDamageSchemaAttributeDefinitionString;

export type DamageSchemaForValidation = Pick<AtlasGqlDamageSchema, 'definitions'>;

const ISO_DATE_FORMAT = 'YYYY-MM-DD';
const NON_DAMAGE_ATTRIBUTE_FIELD_IDS = new Set([
  'IMAGE_NAME',
  'COORDINATES',
  'INSPECTION_DATE',
  'SITE_NAME',
  'TURBINE_NAME',
  'COMPONENT_UNIQUE_NAME',
]);
const NON_DAMAGE_ATTRIBUTE_FIELD_NAMES = new Set([
  'photo file name',
  'coordinates',
  'inspection date',
  'site',
  'turbine',
  'component name',
]);
const LOOKUP_FIELD_ID_PREFIX = 'LOOKUP_';
const NON_VALIDATED_SCHEMA_ATTRIBUTE_NAMES = new Set(['zeitview id']);

const addCellError = (
  cellErrors: Record<string, string[]>,
  schemaFieldId: string,
  message: string
) => {
  cellErrors[schemaFieldId] = [...(cellErrors[schemaFieldId] ?? []), message];
};

const isEmpty = (value: string | undefined) => !value || value.trim() === '';

const definitionAllowsCsvUpload = (definition: DamageSchemaDefinition) =>
  !definition.disabled && definition.state !== AtlasGqlDamageSchemaAttributeState.Disabled;

const shouldSkipSchemaValidation = (definition: DamageSchemaDefinition) =>
  NON_VALIDATED_SCHEMA_ATTRIBUTE_NAMES.has(definition.name.trim().toLowerCase());

export const isDamageLookupSchemaDefinition = ({
  id,
  name,
}: Pick<DamageSchemaDefinition, 'id' | 'name'>) =>
  id.startsWith(LOOKUP_FIELD_ID_PREFIX) ||
  NON_DAMAGE_ATTRIBUTE_FIELD_IDS.has(id) ||
  NON_DAMAGE_ATTRIBUTE_FIELD_NAMES.has(name.trim().toLowerCase());

const getDamageSchemaDefinition = (
  schema: DamageSchemaForValidation | undefined,
  schemaFieldId: string
): DamageSchemaDefinition | undefined => {
  const definition = schema?.definitions?.find(({ id }) => id === schemaFieldId) as
    | DamageSchemaDefinition
    | undefined;

  return definition && !isDamageLookupSchemaDefinition(definition) ? definition : undefined;
};

export const getDamageSchemaStringOptions = (
  schema: DamageSchemaForValidation | undefined,
  schemaFieldId: string
): string[] | undefined => {
  const definition = getDamageSchemaDefinition(schema, schemaFieldId);

  if (
    !definition ||
    shouldSkipSchemaValidation(definition) ||
    !definitionAllowsCsvUpload(definition) ||
    definition.type !== AtlasGqlDamageSchemaAttributeType.String
  ) {
    return undefined;
  }

  return (definition as AtlasGqlDamageSchemaAttributeDefinitionString).options;
};

const validateDateRange = (
  value: string,
  definition: AtlasGqlDamageSchemaAttributeDefinitionDate
) => {
  const date = moment(value, ISO_DATE_FORMAT, true);
  const min = definition.dateMin ? moment(definition.dateMin) : undefined;
  const max = definition.dateMax ? moment(definition.dateMax) : undefined;

  if (!date.isValid()) {
    return `Date must use ${ISO_DATE_FORMAT}.`;
  }

  if (min?.isValid() && date.isBefore(min, 'day')) {
    return `Date must be on or after ${min.format(ISO_DATE_FORMAT)}.`;
  }

  if (max?.isValid() && date.isAfter(max, 'day')) {
    return `Date must be on or before ${max.format(ISO_DATE_FORMAT)}.`;
  }

  return undefined;
};

const validateSchemaDefinitionValue = (
  value: string,
  definition: DamageSchemaDefinition
): string | undefined => {
  if (!definitionAllowsCsvUpload(definition)) {
    return `${definition.name} is not an active Damage Schema attribute.`;
  }

  if (definition.type === AtlasGqlDamageSchemaAttributeType.String) {
    const stringDefinition = definition as AtlasGqlDamageSchemaAttributeDefinitionString;
    return stringDefinition.options.includes(value)
      ? undefined
      : `${definition.name} must be one of: ${stringDefinition.options.join(', ')}.`;
  }

  if (definition.type === AtlasGqlDamageSchemaAttributeType.Number) {
    const numberValue = Number(value);
    const numberDefinition = definition as AtlasGqlDamageSchemaAttributeDefinitionNumber;

    if (!Number.isFinite(numberValue)) {
      return `${definition.name} must be a number.`;
    }

    if (numberValue < numberDefinition.numMin || numberValue > numberDefinition.numMax) {
      return `${definition.name} must be between ${numberDefinition.numMin} and ${numberDefinition.numMax}.`;
    }

    return undefined;
  }

  if (definition.type === AtlasGqlDamageSchemaAttributeType.Date) {
    return validateDateRange(value, definition as AtlasGqlDamageSchemaAttributeDefinitionDate);
  }

  return undefined;
};

const normalizeCsvCellValue = (value: unknown) =>
  String(value ?? '')
    .replace(/\uFEFF/g, '')
    .replace(/\u00A0/g, ' ')
    .trim();

export const isBlankCsvDamageRow = (row: PreviewDamage) =>
  Object.values(row).every(value => normalizeCsvCellValue(value) === '');

export const filterBlankCsvDamageRows = (rows: PreviewDamage[]) =>
  rows.filter(row => !isBlankCsvDamageRow(row));

export const createEditableCsvRows = (rows: PreviewDamage[]): EditableCsvDamageRow[] =>
  rows.map((row, index) => ({
    id: `${index}`,
    originalIndex: index,
    values: Object.fromEntries(
      Object.entries(row).map(([key, value]) => [key, value === undefined ? '' : String(value)])
    ),
    validation: {
      isValid: true,
      cellErrors: {},
    },
  }));

export const validateCsvDamageRow = ({
  row,
  dataFields,
  headerMapping,
  schema,
}: {
  row: EditableCsvDamageRow;
  dataFields: DataField[];
  headerMapping: HeaderMappingEntry[];
  schema?: DamageSchemaForValidation;
}): CsvDamageRowValidation => {
  const cellErrors: Record<string, string[]> = {};

  for (const { fileHeader, schemaFieldId } of headerMapping) {
    const field = dataFields.find(({ key }) => key === schemaFieldId);
    const value = row.values[fileHeader];
    const label = field?.label ?? fileHeader;

    if (field?.required && isEmpty(value)) {
      const message = `${label} is required.`;
      addCellError(cellErrors, schemaFieldId, message);
      continue;
    }

    if (isEmpty(value)) {
      continue;
    }

    if (schemaFieldId === 'COORDINATES') {
      if (!isValidCoordinates(value)) {
        const message = `${label} must be absolute coordinate pairs.`;
        addCellError(cellErrors, schemaFieldId, message);
      }
      continue;
    }

    if (schemaFieldId === 'INSPECTION_DATE') {
      if (!isValidDateFormat(value)) {
        const message = `${label} must use ${ISO_DATE_FORMAT}.`;
        addCellError(cellErrors, schemaFieldId, message);
      }
      continue;
    }

    const definition = getDamageSchemaDefinition(schema, schemaFieldId);

    if (!definition || shouldSkipSchemaValidation(definition)) {
      continue;
    }

    const error = validateSchemaDefinitionValue(value, definition);

    if (error) {
      addCellError(cellErrors, schemaFieldId, error);
    }
  }

  return {
    isValid: Object.keys(cellErrors).length === 0,
    cellErrors,
  };
};

export const validateCsvDamageRows = ({
  rows,
  dataFields,
  headerMapping,
  schema,
}: {
  rows: EditableCsvDamageRow[];
  dataFields: DataField[];
  headerMapping: HeaderMappingEntry[];
  schema?: DamageSchemaForValidation;
}): EditableCsvDamageRow[] =>
  rows.map(row => ({
    ...row,
    validation: validateCsvDamageRow({ row, dataFields, headerMapping, schema }),
  }));
